import { useEffect } from 'react';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Descriptions,
  Divider,
  Form,
  InputNumber,
  Switch,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { ErrorAlert } from '@/components/ErrorAlert';
import { formatDateTime } from '@/lib/format';
import {
  usePlatformSettingsQuery,
  useUpdatePlatformSettingsMutation,
  type PlatformSettingsUpdate,
} from './hooks';

const { Paragraph, Text } = Typography;

interface FormValues {
  balance_threshold_kzt: number;
  balance_control_enabled: boolean;
  sms_global_cap_monthly: number;
  default_tenant_quota_monthly: number;
  default_tenant_daily_quota: number;
}

// Настройки SMS (M5.7) — переехали из платформенных «Настроек» в SMS-центр,
// ближе к балансу и расходу. Порог блокировки + флаг контроля баланса +
// платформенные квоты SMS. PATCH /platform/settings. Быстрый контрол «Настроить
// порог» (ThresholdModal из карточки баланса) правит тот же threshold.
export function SmsSettingsCard(): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<FormValues>();
  const { data, isLoading, error } = usePlatformSettingsQuery();
  const mutation = useUpdatePlatformSettingsMutation();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        balance_threshold_kzt: data.balance_threshold_kzt,
        balance_control_enabled: data.balance_control_enabled,
        sms_global_cap_monthly: data.sms_global_cap_monthly,
        default_tenant_quota_monthly: data.default_tenant_quota_monthly,
        default_tenant_daily_quota: data.default_tenant_daily_quota,
      });
    }
  }, [data, form]);

  const onFinish = (values: FormValues): void => {
    const patch: PlatformSettingsUpdate = {
      balance_threshold_kzt: values.balance_threshold_kzt,
      balance_control_enabled: values.balance_control_enabled,
      sms_global_cap_monthly: values.sms_global_cap_monthly,
      default_tenant_quota_monthly: values.default_tenant_quota_monthly,
      default_tenant_daily_quota: values.default_tenant_daily_quota,
    };
    mutation.mutate(patch, {
      onSuccess: () => {
        void message.success(t('platform.sms_settings.saved'));
      },
    });
  };

  return (
    <Card
      title={t('platform.sms_settings.section_title')}
      variant="outlined"
      data-testid="sms-settings-card"
    >
      {error && (
        <div style={{ marginBottom: 16 }}>
          <ErrorAlert error={error} />
        </div>
      )}

      <Form<FormValues> form={form} layout="vertical" onFinish={onFinish} disabled={isLoading}>
        <Text strong>{t('platform.sms_settings.block_title')}</Text>
        <Paragraph type="secondary" style={{ marginTop: 4 }}>
          {t('platform.sms_settings.block_description')}
        </Paragraph>
        <Form.Item
          label={t('platform.sms_settings.control_enabled')}
          name="balance_control_enabled"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t('platform.sms_settings.threshold')}
          name="balance_threshold_kzt"
          rules={[{ required: true, message: t('platform.sms_settings.required') }]}
        >
          <InputNumber<number>
            min={0}
            step={1000}
            style={{ width: 260 }}
            addonAfter="₸"
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
            parser={(v) => Number((v ?? '').replace(/\s/g, '')) as unknown as number}
          />
        </Form.Item>

        <Divider />

        <Text strong>{t('platform.sms_settings.quota_title')}</Text>
        <Paragraph type="secondary" style={{ marginTop: 4 }}>
          {t('platform.sms_settings.quota_description')}
        </Paragraph>
        <Form.Item
          label={t('platform.sms_settings.global_cap')}
          name="sms_global_cap_monthly"
          rules={[{ required: true, message: t('platform.sms_settings.required') }]}
          extra={t('platform.sms_settings.zero_unlimited')}
        >
          <InputNumber<number> min={0} step={1000} style={{ width: 260 }} />
        </Form.Item>
        <Form.Item
          label={t('platform.sms_settings.tenant_quota')}
          name="default_tenant_quota_monthly"
          rules={[{ required: true, message: t('platform.sms_settings.required') }]}
          extra={t('platform.sms_settings.zero_unlimited')}
        >
          <InputNumber<number> min={0} step={100} style={{ width: 260 }} />
        </Form.Item>
        <Form.Item
          label={t('platform.sms_settings.tenant_daily_quota')}
          name="default_tenant_daily_quota"
          rules={[{ required: true, message: t('platform.sms_settings.required') }]}
          extra={t('platform.sms_settings.zero_unlimited')}
        >
          <InputNumber<number> min={0} step={10} style={{ width: 260 }} />
        </Form.Item>

        {mutation.error && (
          <div style={{ marginBottom: 16 }}>
            <ErrorAlert error={mutation.error} />
          </div>
        )}

        <Button
          type="primary"
          htmlType="submit"
          loading={mutation.isPending}
          data-testid="sms-settings-save"
        >
          {t('common.save')}
        </Button>
      </Form>

      <Divider />

      <Text strong>{t('platform.sms_settings.sync_title')}</Text>
      <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 12 }}>
        {t('platform.sms_settings.sync_description')}
      </Paragraph>
      <Descriptions column={1} size="small">
        <Descriptions.Item label={t('platform.sms_settings.updated_at')}>
          {formatDateTime(data?.updated_at)}
        </Descriptions.Item>
      </Descriptions>
      <Alert
        type="info"
        showIcon
        style={{ marginTop: 12 }}
        message={t('platform.sms_settings.sync_note')}
      />
    </Card>
  );
}
